import {
  computed,
  effect,
  ErrorHandler,
  inject,
  resource,
  ResourceOptions,
  ResourceStatus,
  Signal,
} from '@angular/core';
import { isNoResponseHttpError } from '@fc-core/errors/http-error.utils';

/**
 * A `resource` with template-safe reads: `value()` returns `undefined` in
 * error state instead of throwing `ResourceValueError`, so one failed
 * request can't break the surrounding render. The error model stays
 * intact — `error()` and `status()` report failures as usual, and every
 * loader error except a no-response (status 0) HTTP failure is also
 * forwarded to the global `ErrorHandler` so failures are never silent.
 */
export interface StoreResource<T> {
  readonly value: Signal<T | undefined>;
  readonly status: Signal<ResourceStatus>;
  readonly error: Signal<Error | undefined>;
  readonly isLoading: Signal<boolean>;
  hasValue(): boolean;
  reload(): boolean;
}

export interface StoreResourceConfig {
  /** Forward loader errors to the global ErrorHandler. Defaults to true. */
  reportErrors?: boolean;
}

/**
 * Drop-in replacement for `resource()` in stores. Must be called in an
 * injection context (a store field initializer is one).
 *
 *   readonly engineHours = storeResource({
 *     params: () => this.selectors.dateRange(),
 *     loader: ({ params }) => this.api.getEngineHours(params),
 *   });
 */
export function storeResource<T, R>(
  options: ResourceOptions<T, R>,
  config?: StoreResourceConfig,
): StoreResource<T> {
  const errorHandler = inject(ErrorHandler);
  const res = resource(options);

  if (config?.reportErrors !== false) {
    effect(() => {
      const error = res.error();
      if (error && !isNoResponseHttpError(error)) {
        errorHandler.handleError(error);
      }
    });
  }

  return {
    value: computed(() => (res.hasValue() ? res.value() : undefined)),
    status: res.status,
    error: res.error,
    isLoading: res.isLoading,
    hasValue: () => res.hasValue(),
    reload: () => res.reload(),
  };
}
